/**
 * What each exported record type means, embedded in the manifest of every
 * device history export. The export's reader is usually an AI tool with no
 * access to this codebase, so this glossary is what turns `{"event":"source_dwell",
 * "data":{"from":"HDMI1","dwell_s":812}}` into something it can reason about.
 *
 * RULE: a new event_type emitted anywhere (uC via uc-bridge, usage-aggregator,
 * the jobs agent, the admin API, the event dispatcher) must be added here.
 * An undocumented type still exports; the manifest lists it under
 * `undocumented_types` so the gap is visible rather than silent.
 */

export interface GlossaryEntry {
  /** One line: what happened. */
  meaning: string;
  /** Who writes it. */
  origin: string;
  /** Key `data` fields → meaning. Not exhaustive; unknown fields pass through. */
  fields?: Record<string, string>;
}

export const EVENT_GLOSSARY: Record<string, GlossaryEntry> = {
  // ── demo usage (usage-aggregator on the device) ──────────────────────────
  demo_session: {
    meaning: 'One demo session ended: the demo was Active and then left Active. Sessions are the unit of "a demo was given".',
    origin: 'device usage-aggregator',
    fields: {
      active_s: 'seconds the demo was Active',
      suspended_s: 'seconds it was Suspended within the session',
      view_count: 'views visited',
      content_play_count: 'videos/audio played',
      reached_feedback: 'true if the presenter reached the feedback screen',
    },
  },
  view_visit: {
    meaning: 'The presenter left a UI view (menu, player, product page…) during an Active session.',
    origin: 'device usage-aggregator',
    fields: { view: 'view name', dwell_ms: 'time spent in the view', keys: 'remote-key presses by key name' },
  },
  content_play: {
    meaning: 'A video or audio clip finished or was stopped.',
    origin: 'device usage-aggregator',
    fields: { id: 'content id / file stem', kind: 'video | audio', watched_ms: 'time watched', completed: 'played to the end' },
  },
  feedback: {
    meaning: 'The audience submitted feedback ratings at the end of a demo.',
    origin: 'device usage-aggregator',
    fields: { ratings: 'list of integer ratings' },
  },
  product_view: {
    meaning: 'A product was focused in the carousel or opened in detail, then left.',
    origin: 'device usage-aggregator',
    fields: { product_model: 'product', source: 'carousel | detail', dwell_ms: 'time focused' },
  },
  feature_view: {
    meaning: 'A product feature popup was opened and closed.',
    origin: 'device usage-aggregator',
    fields: { product_model: 'product', pill_id: 'feature id', pill_label: 'feature label', dwell_ms: 'time open' },
  },
  gallery_view: {
    meaning: 'A product image gallery was opened and closed.',
    origin: 'device usage-aggregator',
    fields: { product_model: 'product', gallery_index: 'last image index', dwell_ms: 'time open' },
  },
  compare_view: {
    meaning: 'The product comparison view was opened and closed.',
    origin: 'device usage-aggregator',
    fields: { dwell_ms: 'time open' },
  },
  source_dwell: {
    meaning: 'The projector input source changed. Not tied to a session.',
    origin: 'device usage-aggregator (also synthesised at boot by the cloud)',
    fields: { from: 'previous source label', to: 'new source label', dwell_s: 'seconds spent on the previous source' },
  },

  // ── projector / microcontroller (uC → uc-bridge) ─────────────────────────
  boot: {
    meaning: 'The Artome microcontroller (projector controller) booted.',
    origin: 'uC',
    fields: { fw_ver: 'uC firmware version' },
  },
  state_change: {
    meaning: 'Projector system state changed.',
    origin: 'uC (also synthesised at boot by the cloud; ids starting "bootsync-")',
    fields: { state: 'new sys_state — see enums.sys_state' },
  },
  demo_mode_change: {
    meaning: 'Demo mode changed.',
    origin: 'uC (also synthesised at boot by the cloud)',
    fields: { demo_mode: 'new demo mode — see enums.demo_mode' },
  },
  error: {
    meaning: 'The projector reported an error condition.',
    origin: 'uC',
    fields: { code: 'error code — see enums.err_state', message: 'error text, when the uC supplies one' },
  },
  limiter_active: {
    meaning: 'A projector output limiter engaged (reserved; not yet emitted by firmware).',
    origin: 'uC',
  },

  // ── connectivity ──────────────────────────────────────────────────────────
  presence: {
    meaning: 'The device connected to or disconnected from AWS IoT.',
    origin: 'AWS IoT lifecycle events; the cloud also writes a synthetic disconnect when a device goes silent',
    fields: {
      kind: 'connected | disconnected',
      reason: 'AWS disconnect reason; "synthesized-from-last-seen" = inferred from silence, not reported by AWS',
    },
  },

  // ── operator actions and their outcomes ───────────────────────────────────
  settings_change: {
    meaning: 'An operator changed device settings in the Portal (the request).',
    origin: 'Portal admin API',
    fields: { actor: 'who', category: 'settings category', keys: 'changed keys', values: 'new values', reason: 'operator note' },
  },
  settings_applied: {
    meaning: 'The device applied a settings write (the outcome of a settings_change).',
    origin: 'device uc-bridge',
    fields: { keys: 'category.key list', status: '0 = ok', corr_id: 'correlation id' },
  },
  settings_rejected: {
    meaning: 'The device refused some or all of a settings write.',
    origin: 'device uc-bridge',
    fields: { keys: 'category.key list', failed: 'per-key error codes', error: '"timeout" if the uC never answered' },
  },
  sim_limit_change: {
    meaning: 'An operator changed the cellular SIM data limit.',
    origin: 'Portal admin API',
    fields: { actor: 'who', prev_total: 'previous limit (bytes)', new_total: 'new limit (bytes)', used_bytes: 'usage at the time', reason: 'operator note' },
  },
  remote_reboot: {
    meaning: 'The device executed an operator-requested remote reboot.',
    origin: 'device jobs agent',
    fields: { requestedBy: 'who', jobId: 'IoT job id', reason: 'operator note', force: 'interrupted an active demo' },
  },

  // ── non-event sources in the export ──────────────────────────────────────
  location_fix: {
    meaning: 'The cloud resolved a new device position (only recorded when the device moved or on first fix).',
    origin: 'geolocation resolver (source = locations)',
    fields: {
      lat: 'latitude', lon: 'longitude', accuracy_m: 'accuracy radius in metres',
      source: 'what the fix is based on (wifi, wifi+cell, cell…)',
      address: 'resolved address (may be locality-level on coarse fixes)',
      place_name: 'best-guess business/site at the position', place_candidates: 'alternatives, nearest first',
    },
  },
  release_deploy: {
    meaning: 'A software release was deployed to this device directly (source = releases).',
    origin: 'Portal Releases page',
    fields: {
      release_id: 'public release ID (YYYY.MM[.N]); null for an internal/dev deploy',
      release_label: 'part versions, e.g. "os:2.6.1+app:1.21.0"',
      parts: 'resolved part set', force: 'deployed with force', job_status: 'last-known IoT job status',
      superseded_at: 'when a later deploy replaced it (null = still current)', created_by: 'who',
    },
  },
};
